import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Doctors.css';
import { getApiBaseUrl } from '../../services/api';
import { emitDataChange, onDataChange, DATA_TYPES, shouldRefresh } from '../../services/dataEvents';

const Doctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [newDoctor, setNewDoctor] = useState({
    name: '',
    specialization: '',
    experience: '',
    preferredLocations: [],
    availability: 'full-time',
    maxShiftsPerMonth: 20
  });
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [hospitalSlots, setHospitalSlots] = useState([]);
  const [doctorSummary, setDoctorSummary] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiStartDate, setAiStartDate] = useState('');
  const [aiEndDate, setAiEndDate] = useState('');

  const apiUrl = getApiBaseUrl();
  
  const loadHospitals = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/hospitals`);
      setHospitals(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error fetching hospitals:', e);
      setHospitals([]);
    }
  }, [apiUrl]);

  const specializations = [
    'Emergency Medicine',
    'Internal Medicine',
    'Surgery',
    'Pediatrics',
    'Cardiology',
    'Neurology',
    'Oncology',
    'Psychiatry',
    'Radiology',
    'Anesthesiology',
    'Orthopedics',
    'Dermatology'
  ];

  const fetchDoctors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/api/doctors`);
      setDoctors(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchDoctors();
    loadHospitals();
  }, [fetchDoctors, loadHospitals]);

  useEffect(() => {
    return onDataChange((type) => {
      if (shouldRefresh(type, [DATA_TYPES.DOCTORS, DATA_TYPES.HOSPITALS, DATA_TYPES.GROUP, DATA_TYPES.ALL])) {
        fetchDoctors();
        loadHospitals();
      }
    });
  }, [fetchDoctors, loadHospitals]);

  const handleAddDoctor = async (e) => {
    e.preventDefault();
    if (!newDoctor.name.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${apiUrl}/api/doctors`, newDoctor);
      setDoctors([...doctors, response.data]);
      emitDataChange(DATA_TYPES.DOCTORS);
      setNewDoctor({
        name: '',
        specialization: '',
        experience: '',
        preferredLocations: [],
        availability: 'full-time',
        maxShiftsPerMonth: 20
      });
    } catch (error) {
      console.error('Error adding doctor:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateDoctor = async (e) => {
    e.preventDefault();
    if (!editingDoctor.name.trim()) return;

    setIsSubmitting(true);
    try {
      const doctorId = editingDoctor.id || editingDoctor._id;
      const response = await axios.put(`${apiUrl}/api/doctors/${doctorId}`, editingDoctor);
      setDoctors(doctors.map(doc => (doc.id || doc._id) === doctorId ? response.data : doc));
      setEditingDoctor(null);
      emitDataChange(DATA_TYPES.DOCTORS);
    } catch (error) {
      console.error('Error updating doctor:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDoctor = async (doctorId) => {
    if (!window.confirm('Are you sure you want to delete this doctor?')) return;

    try {
      await axios.delete(`${apiUrl}/api/doctors/${doctorId}`);
      setDoctors(doctors.filter(doc => (doc.id || doc._id) !== doctorId));
      emitDataChange(DATA_TYPES.DOCTORS);
    } catch (error) {
      console.error('Error deleting doctor:', error);
    }
  };

  const generateAISchedule = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${apiUrl}/api/ai/generate-schedule`, {
        month: selectedMonth,
        year: selectedYear,
        doctors: doctors,
        hospitals: hospitals,
        prompt: aiPrompt || undefined,
        start_date: aiStartDate || undefined,
        end_date: aiEndDate || undefined,
      });
      const suggestions = response.data.suggestions || [];
      setAiSuggestions(suggestions);
      if (suggestions.length > 0) {
        setHospitalSlots(suggestions[0].hospital_slots || []);
        setDoctorSummary(suggestions[0].doctor_summary || []);
      }
    } catch (error) {
      console.error('Error generating AI schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyAISuggestion = async (suggestion) => {
    try {
      await axios.post(`${apiUrl}/api/shifts/batch`, suggestion.shifts);
      setAiSuggestions([]);
      emitDataChange(DATA_TYPES.SHIFTS);
      window.location.hash = '#schedule';
    } catch (error) {
      console.error('Error applying AI suggestion:', error);
    }
  };

  const toggleLocation = (location) => {
    if (editingDoctor) {
      const updated = editingDoctor.preferredLocations.includes(location)
        ? editingDoctor.preferredLocations.filter(loc => loc !== location)
        : [...editingDoctor.preferredLocations, location];
      setEditingDoctor({ ...editingDoctor, preferredLocations: updated });
    } else {
      const updated = newDoctor.preferredLocations.includes(location)
        ? newDoctor.preferredLocations.filter(loc => loc !== location)
        : [...newDoctor.preferredLocations, location];
      setNewDoctor({ ...newDoctor, preferredLocations: updated });
    }
  };

  const thStyle = { padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' };
  const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', fontSize: 13 };

  return (
    <div className="doctors-container">
      <div className="doctors-header">
        <h2>Registered Doctors</h2>
        <p>Manage doctors and generate AI-optimized schedules</p>
      </div>

      <div className="doctors-content">
        {/* Add New Doctor Form */}
        <div className="add-doctor-section">
          <h3>Add New Doctor</h3>
          <form onSubmit={handleAddDoctor} className="doctor-form">
            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={newDoctor.name}
                  onChange={(e) => setNewDoctor({ ...newDoctor, name: e.target.value })}
                  placeholder="Enter doctor's full name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Specialization</label>
                <select
                  value={newDoctor.specialization}
                  onChange={(e) => setNewDoctor({ ...newDoctor, specialization: e.target.value })}
                  required
                >
                  <option value="">Select specialization</option>
                  {specializations.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Experience (years)</label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={newDoctor.experience}
                  onChange={(e) => setNewDoctor({ ...newDoctor, experience: e.target.value })}
                  placeholder="Years of experience"
                />
              </div>
              <div className="form-group">
                <label>Availability</label>
                <select
                  value={newDoctor.availability}
                  onChange={(e) => setNewDoctor({ ...newDoctor, availability: e.target.value })}
                >
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="weekends">Weekends Only</option>
                  <option value="nights">Night Shifts</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Preferred Hospital Locations</label>
              <div className="location-checkboxes">
                {hospitals.map(hospital => (
                  <label key={hospital.code} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newDoctor.preferredLocations.includes(hospital.code)}
                      onChange={() => toggleLocation(hospital.code)}
                    />
                    {hospital.name} ({hospital.code})
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Max Shifts per Month</label>
              <input
                type="number"
                min="1"
                max="31"
                value={newDoctor.maxShiftsPerMonth}
                onChange={(e) => setNewDoctor({ ...newDoctor, maxShiftsPerMonth: parseInt(e.target.value) })}
              />
            </div>

            <button type="submit" disabled={isSubmitting} className="submit-btn">
              {isSubmitting ? 'Adding...' : 'Add Doctor'}
            </button>
          </form>
        </div>

        {/* AI Schedule Generation */}
        <div className="ai-schedule-section">
          <h3>AI-Powered Schedule Generation</h3>
          <div className="ai-controls">
            <div className="month-selector">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2025, i).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {Array.from({ length: 5 }, (_, i) => (
                  <option key={2025 + i} value={2025 + i}>{2025 + i}</option>
                ))}
              </select>
            </div>
            <button
              onClick={generateAISchedule}
              disabled={loading || doctors.length === 0}
              className="ai-generate-btn"
            >
              {loading ? 'Generating...' : '🤖 Generate AI Schedule'}
            </button>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, opacity: 0.8 }}>Start Date (optional)</label>
                <input
                  type="date"
                  value={aiStartDate}
                  onChange={(e) => setAiStartDate(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, opacity: 0.8 }}>End Date (optional)</label>
                <input
                  type="date"
                  value={aiEndDate}
                  onChange={(e) => setAiEndDate(e.target.value)}
                />
              </div>
            </div>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Doctor/Admin instructions (optional)</label>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={`Example: From 2026-04-01 to 2026-04-30. Malik 14 shifts in MPH. Exclude weekends.`}
              style={{ width: '100%', minHeight: 90, marginTop: 6 }}
            />
          </div>

          {aiSuggestions.length > 0 && (
            <div className="ai-suggestions">
              <h4>AI Suggestions</h4>
              {aiSuggestions.map((suggestion, index) => (
                <div key={index} className="ai-suggestion">
                  <div className="suggestion-header">
                    <h5>Option {index + 1}</h5>
                    <span className="score">Score: {suggestion.score}%</span>
                  </div>
                  <div className="suggestion-details">
                    <p><strong>Coverage:</strong> {suggestion.coverage}% of shifts covered</p>
                    <p><strong>Balance:</strong> {suggestion.balance}% workload distribution</p>
                    <p><strong>Preferences:</strong> {suggestion.preferences}% preference satisfaction</p>
                  </div>

                  {/* Hospital Slot Usage */}
                  {hospitalSlots.length > 0 && (
                    <div className="ai-hospital-slots" style={{ marginTop: 12 }}>
                      <h5 style={{ marginBottom: 8 }}>Hospital Slot Capacity</h5>
                      <table className="ai-slots-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#f0f4ff' }}>
                            <th style={thStyle}>Hospital</th>
                            <th style={thStyle}>Monthly Slots</th>
                            <th style={thStyle}>Existing</th>
                            <th style={thStyle}>AI Proposed</th>
                            <th style={thStyle}>Total</th>
                            <th style={thStyle}>Remaining</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hospitalSlots.map(hs => (
                            <tr key={hs.code}>
                              <td style={tdStyle}><strong>{hs.code}</strong> {hs.name !== hs.code ? `(${hs.name})` : ''}</td>
                              <td style={tdStyle}>{hs.monthly_slots}</td>
                              <td style={tdStyle}>{hs.existing_bookings}</td>
                              <td style={tdStyle}>{hs.ai_proposed}</td>
                              <td style={tdStyle}>{hs.total_after_ai}</td>
                              <td style={{
                                ...tdStyle,
                                color: hs.remaining > 0 ? '#16a34a' : '#dc2626',
                                fontWeight: 'bold'
                              }}>
                                {hs.remaining}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Doctor Assignment Summary */}
                  {doctorSummary.length > 0 && (
                    <div className="ai-doctor-summary" style={{ marginTop: 12 }}>
                      <h5 style={{ marginBottom: 8 }}>Doctor Assignments</h5>
                      <table className="ai-doctor-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#f0f4ff' }}>
                            <th style={thStyle}>Doctor</th>
                            <th style={thStyle}>Max Shifts</th>
                            <th style={thStyle}>Assigned</th>
                          </tr>
                        </thead>
                        <tbody>
                          {doctorSummary.map(ds => (
                            <tr key={ds.name}>
                              <td style={tdStyle}>{ds.display_name || ds.name}</td>
                              <td style={tdStyle}>{ds.max}</td>
                              <td style={{
                                ...tdStyle,
                                color: ds.assigned === ds.max ? '#16a34a' : ds.assigned > 0 ? '#ca8a04' : '#94a3b8',
                                fontWeight: 'bold'
                              }}>{ds.assigned}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="suggestion-actions" style={{ marginTop: 12 }}>
                    <button
                      onClick={() => applyAISuggestion(suggestion)}
                      className="apply-btn"
                    >
                      Apply This Schedule
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Doctors List */}
        <div className="doctors-list-section">
          <h3>Registered Doctors ({doctors.length})</h3>
          {loading ? (
            <div className="loading">Loading doctors...</div>
          ) : (
            <div className="doctors-grid">
              {doctors.map(doctor => (
                <div key={doctor.id || doctor._id} className="doctor-card">
                  <div className="doctor-header">
                    <h4>{doctor.name}</h4>
                    <div className="doctor-actions">
                      <button
                        onClick={() => setEditingDoctor(doctor)}
                        className="edit-btn"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteDoctor(doctor.id || doctor._id)}
                        className="delete-btn"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="doctor-details">
                    <p><strong>Specialization:</strong> {doctor.specialization}</p>
                    <p><strong>Experience:</strong> {doctor.experience} years</p>
                    <p><strong>Availability:</strong> {doctor.availability}</p>
                    <p><strong>Max Shifts:</strong> {doctor.maxShiftsPerMonth}/month</p>
                    <p><strong>Preferred Locations:</strong></p>
                    <div className="location-tags">
                      {doctor.preferredLocations.map(loc => (
                        <span key={loc} className="location-tag">{loc}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Doctor Modal */}
        {editingDoctor && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Edit Doctor</h3>
              <form onSubmit={handleUpdateDoctor} className="doctor-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={editingDoctor.name}
                      onChange={(e) => setEditingDoctor({ ...editingDoctor, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Specialization</label>
                    <select
                      value={editingDoctor.specialization}
                      onChange={(e) => setEditingDoctor({ ...editingDoctor, specialization: e.target.value })}
                      required
                    >
                      {specializations.map(spec => (
                        <option key={spec} value={spec}>{spec}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Experience (years)</label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={editingDoctor.experience}
                      onChange={(e) => setEditingDoctor({ ...editingDoctor, experience: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Availability</label>
                    <select
                      value={editingDoctor.availability}
                      onChange={(e) => setEditingDoctor({ ...editingDoctor, availability: e.target.value })}
                    >
                      <option value="full-time">Full Time</option>
                      <option value="part-time">Part Time</option>
                      <option value="weekends">Weekends Only</option>
                      <option value="nights">Night Shifts</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Preferred Hospital Locations</label>
                  <div className="location-checkboxes">
                    {hospitals.map(hospital => (
                      <label key={hospital.code} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={editingDoctor.preferredLocations.includes(hospital.code)}
                          onChange={() => toggleLocation(hospital.code)}
                        />
                        {hospital.name} ({hospital.code})
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Max Shifts per Month</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={editingDoctor.maxShiftsPerMonth}
                    onChange={(e) => setEditingDoctor({ ...editingDoctor, maxShiftsPerMonth: parseInt(e.target.value) })}
                  />
                </div>

                <div className="modal-actions">
                  <button type="submit" disabled={isSubmitting} className="submit-btn">
                    {isSubmitting ? 'Updating...' : 'Update Doctor'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingDoctor(null)}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Doctors;
